/* =====================================================================
 * docxParser.ts — OOXML(DOCX) → 내부 모델 파서
 * JSZip + DOMParser 기반으로 document.xml, styles.xml, numbering.xml,
 * media/*, _rels/*.rels 를 파싱하여 편집 가능한 내부 모델로 변환
 * ===================================================================== */

import JSZip from 'jszip';

/* ------------------------------------------------------------------ */
/* Types — 내부 문서 모델                                                */
/* ------------------------------------------------------------------ */

export interface DocxPageLayout {
  /** 페이지 너비 (twips) */
  width: number;
  /** 페이지 높이 (twips) */
  height: number;
  /** 상단 여백 (twips) */
  marginTop: number;
  /** 하단 여백 (twips) */
  marginBottom: number;
  /** 왼쪽 여백 (twips) */
  marginLeft: number;
  /** 오른쪽 여백 (twips) */
  marginRight: number;
}

export interface DocxDocument {
  body: DocxBlock[];
  styles: Map<string, DocxStyle>;
  numbering: Map<string, DocxNumbering>;
  media: Map<string, string>; // rId → base64 data URL
  rawParts: Map<string, string>; // partPath → raw XML string (패치용 보존)
  zip: JSZip; // 원본 ZIP 참조 (export 시 사용)
  /** 페이지 레이아웃 (sectPr에서 파싱) */
  pageLayout: DocxPageLayout;
  docDefaults?: {
    fontSize?: number;      // half-points (e.g. 22 = 11pt)
    fontFamily?: string;
    spacing?: { before?: number; after?: number; line?: number; lineRule?: string };
    alignment?: string;
  };
}

export type DocxBlock = DocxParagraph | DocxTable;

export interface DocxParagraph {
  type: 'paragraph';
  id: string;
  styleId?: string;
  alignment?: string;
  indent?: { left?: number; right?: number; firstLine?: number; hanging?: number };
  spacing?: { before?: number; after?: number; line?: number; lineRule?: string };
  /** 문단 기본 런 속성 (w:pPr/w:rPr) */
  runProps?: Partial<DocxRun>;
  runs: DocxRun[];
  numbering?: { numId: string; level: number; index: number };
  dirty?: boolean;
}

export interface DocxRun {
  type: 'text' | 'image' | 'break' | 'tab';
  id: string;
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  fontSize?: number; // half-points (w:sz 값 그대로)
  fontFamily?: string;
  color?: string;
  highlight?: string;
  imageId?: string; // rId (media map 참조)
  imageWidth?: number; // EMU → px 변환 후
  imageHeight?: number;
}

export interface DocxTable {
  type: 'table';
  id: string;
  borders?: {
    top?: DocxBorder;
    bottom?: DocxBorder;
    left?: DocxBorder;
    right?: DocxBorder;
    insideH?: DocxBorder;
    insideV?: DocxBorder;
  };
  rows: DocxTableRow[];
  dirty?: boolean;
}

export interface DocxTableRow {
  cells: DocxTableCell[];
}

export interface DocxTableCell {
  paragraphs: DocxParagraph[];
  colSpan?: number;
  rowSpan?: number;
  shading?: string;
  width?: number;
  borders?: {
    top?: DocxBorder;
    bottom?: DocxBorder;
    left?: DocxBorder;
    right?: DocxBorder;
  };
}

export interface DocxBorder {
  style?: string;
  color?: string;
  size?: number;
}

export interface DocxStyle {
  id: string;
  name?: string;
  basedOn?: string;
  fontSize?: number;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  alignment?: string;
  indent?: { left?: number; right?: number; firstLine?: number; hanging?: number };
  spacing?: { before?: number; after?: number; line?: number; lineRule?: string };
  /** 스타일의 w:numPr/w:numId 원본 값 (basedOn 해석 전). */
  numbering?: { numId?: string };
}

export interface DocxNumbering {
  numId: string;
  levels: Map<number, DocxNumberingLevel>;
}

export interface DocxNumberingLevel {
  format: string; // decimal, bullet, lowerLetter, upperLetter, lowerRoman, upperRoman, decimalEnclosedCircle, etc.
  text: string; // e.g. "%1.", "%1)"
  indent?: number;
  start: number;        // start value (e.g. 1)
  hanging?: number;     // hanging indent in twips→px
  /** 스타일 기반 목록의 level을 선택하는 w:lvl/w:pStyle. */
  paragraphStyleId?: string;
}

/* ------------------------------------------------------------------ */
/* XML 네임스페이스                                                      */
/* ------------------------------------------------------------------ */

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const WP_NS = 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const PIC_NS = 'http://schemas.openxmlformats.org/drawingml/2006/picture';
const REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

/* ------------------------------------------------------------------ */
/* 유틸리티                                                              */
/* ------------------------------------------------------------------ */

let _idCounter = 0;
function generateId(): string {
  return `docx_${Date.now()}_${++_idCounter}`;
}

/** EMU (English Metric Units) → px 변환 (1 EMU = 1/914400 inch, 96 DPI) */
function emuToPx(emu: number): number {
  return Math.round((emu / 914400) * 96);
}

/** twips → px 변환 (1 twip = 1/1440 inch) */
export function twipsToPx(twips: number): number {
  return Math.round((twips / 1440) * 96);
}

/** half-points → px (참고용: 1pt = 2 half-points) */
function halfPointsToPx(hp: number): number {
  return hp / 2;
}

/** W 네임스페이스 요소 가져오기 */
function getW(parent: Element, localName: string): Element | null {
  return parent.getElementsByTagNameNS(W_NS, localName)[0] || null;
}

/** W 네임스페이스 요소 모두 가져오기 */
function getAllW(parent: Element, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS(W_NS, localName));
}

/** 속성 값 가져오기 (w:val) */
function getWVal(parent: Element, localName: string): string | undefined {
  const el = getW(parent, localName);
  if (!el) return undefined;
  return el.getAttributeNS(W_NS, 'val') || el.getAttribute('w:val') || undefined;
}

/** 직접 자식 요소만 필터 */
function directChildren(parent: Element, ns: string, localName: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes[i];
    if (
      child.nodeType === 1 &&
      (child as Element).localName === localName &&
      (child as Element).namespaceURI === ns
    ) {
      result.push(child as Element);
    }
  }
  return result;
}

/** 직접 자식 요소의 w:val 값만 가져온다 (pPrChange 내부 과거 값 제외). */
function getDirectWVal(parent: Element, localName: string): string | undefined {
  const el = directChildren(parent, W_NS, localName)[0];
  return el?.getAttributeNS(W_NS, 'val') || el?.getAttribute('w:val') || undefined;
}

interface ParsedNumberingProperties {
  numId?: string;
  level?: number;
}

/** w:pPr 바로 아래의 w:numPr를 partial 값으로 파싱한다. */
function parseNumberingProperties(
  pPr: Element,
): ParsedNumberingProperties | undefined {
  const numPr = directChildren(pPr, W_NS, 'numPr')[0];
  if (!numPr) return undefined;

  const properties: ParsedNumberingProperties = {};
  const numId = getDirectWVal(numPr, 'numId');
  if (numId !== undefined) properties.numId = numId;

  const levelValue = getDirectWVal(numPr, 'ilvl');
  if (levelValue !== undefined) {
    const level = Number.parseInt(levelValue, 10);
    if (Number.isFinite(level)) properties.level = level;
  }

  // 빈 numPr도 "직접 지정됨"을 뜻하므로 undefined와 구분한다.
  return properties;
}

/** basedOn 체인의 스타일 번호 속성을 부모 → 자식 순서로 병합한다. */
function resolveStyleNumbering(
  styleId: string | undefined,
  styles: Map<string, DocxStyle>,
  visited = new Set<string>(),
): NonNullable<DocxStyle['numbering']> | undefined {
  if (!styleId || visited.has(styleId)) return undefined;

  const style = styles.get(styleId);
  if (!style) return undefined;

  visited.add(styleId);
  const inherited = resolveStyleNumbering(style.basedOn, styles, visited);
  if (style.numbering === undefined) return inherited;

  return { ...inherited, ...style.numbering };
}

/** numbering.xml의 w:lvl/w:pStyle을 현재 스타일의 basedOn 체인과 매칭한다. */
function resolveStyleNumberingLevel(
  styleId: string | undefined,
  styles: Map<string, DocxStyle>,
  numberingDefinition: DocxNumbering | undefined,
): number | undefined {
  if (!styleId || !numberingDefinition) return undefined;

  const visited = new Set<string>();
  let currentStyleId: string | undefined = styleId;
  while (currentStyleId && !visited.has(currentStyleId)) {
    visited.add(currentStyleId);

    for (const [level, definition] of numberingDefinition.levels) {
      if (definition.paragraphStyleId === currentStyleId) return level;
    }

    currentStyleId = styles.get(currentStyleId)?.basedOn;
  }

  return undefined;
}

/* ------------------------------------------------------------------ */
/* 메인 파서                                                             */
/* ------------------------------------------------------------------ */

export async function parseDocx(buffer: ArrayBuffer): Promise<DocxDocument> {
  _idCounter = 0;
  const zip = await JSZip.loadAsync(buffer);
  const rawParts = new Map<string, string>();

  // 1) document.xml 파싱
  const docXml = await readPart(zip, 'word/document.xml');
  if (!docXml) throw new Error('word/document.xml을 찾을 수 없습니다.');
  rawParts.set('word/document.xml', docXml);
  const docDom = new DOMParser().parseFromString(docXml, 'application/xml');

  // 2) theme1.xml 파싱 (테마 폰트)
  const themeXml = await readPart(zip, 'word/theme/theme1.xml');
  const themeFonts = themeXml ? parseThemeFonts(themeXml) : { major: undefined, minor: undefined };

  // 3) styles.xml 파싱 (docDefaults 포함)
  const stylesXml = await readPart(zip, 'word/styles.xml');
  if (stylesXml) rawParts.set('word/styles.xml', stylesXml);
  const { styles, docDefaults } = stylesXml
    ? parseStyles(stylesXml, themeFonts)
    : { styles: new Map<string, DocxStyle>(), docDefaults: undefined };

  // 5) numbering.xml 파싱
  const numberingXml = await readPart(zip, 'word/numbering.xml');
  if (numberingXml) rawParts.set('word/numbering.xml', numberingXml);
  const numbering = numberingXml ? parseNumbering(numberingXml) : new Map<string, DocxNumbering>();

  // 6) document.xml.rels 파싱 (이미지 등 관계 매핑)
  const relsXml = await readPart(zip, 'word/_rels/document.xml.rels');
  const rels = relsXml ? parseRels(relsXml) : new Map<string, string>();

  // 7) 미디어 파일 로드 (base64)
  const media = await loadMedia(zip, rels);

  // 8) 본문 파싱 (numbering counter tracking 포함)
  const bodyEl = docDom.getElementsByTagNameNS(W_NS, 'body')[0];
  const body = bodyEl ? parseBody(bodyEl, rels, numbering, styles) : [];

  // 9) 페이지 레이아웃 파싱 (sectPr → pgSz, pgMar)
  const pageLayout = parseSectPr(docDom);

  return { body, styles, numbering, media, rawParts, zip, pageLayout, docDefaults };
}

/* ------------------------------------------------------------------ */
/* sectPr 파싱 (페이지 크기 + 여백)                                       */
/* ------------------------------------------------------------------ */

/** A4 기본값 (twips): 210mm × 297mm, 여백 2.54cm (1in) */
const DEFAULT_PAGE_LAYOUT: DocxPageLayout = {
  width: 11906,    // A4 width in twips
  height: 16838,   // A4 height in twips
  marginTop: 1440, // 1 inch = 2.54cm
  marginBottom: 1440,
  marginLeft: 1440,
  marginRight: 1440,
};

function parseSectPr(docDom: Document): DocxPageLayout {
  // <w:sectPr> 은 보통 <w:body> 의 마지막 자식
  const sectPrEls = docDom.getElementsByTagNameNS(W_NS, 'sectPr');
  if (sectPrEls.length === 0) return { ...DEFAULT_PAGE_LAYOUT };

  // 마지막(또는 유일한) sectPr 사용
  const sectPr = sectPrEls[sectPrEls.length - 1];
  const layout = { ...DEFAULT_PAGE_LAYOUT };

  // <w:pgSz w:w="11906" w:h="16838"/>
  const pgSz = getW(sectPr, 'pgSz');
  if (pgSz) {
    const w = pgSz.getAttributeNS(W_NS, 'w') || pgSz.getAttribute('w:w');
    const h = pgSz.getAttributeNS(W_NS, 'h') || pgSz.getAttribute('w:h');
    if (w) layout.width = parseInt(w, 10);
    if (h) layout.height = parseInt(h, 10);
  }

  // <w:pgMar w:top="1701" w:right="1440" w:bottom="1440" w:left="1440" .../>
  const pgMar = getW(sectPr, 'pgMar');
  if (pgMar) {
    const top = pgMar.getAttributeNS(W_NS, 'top') || pgMar.getAttribute('w:top');
    const bottom = pgMar.getAttributeNS(W_NS, 'bottom') || pgMar.getAttribute('w:bottom');
    const left = pgMar.getAttributeNS(W_NS, 'left') || pgMar.getAttribute('w:left');
    const right = pgMar.getAttributeNS(W_NS, 'right') || pgMar.getAttribute('w:right');
    if (top) layout.marginTop = parseInt(top, 10);
    if (bottom) layout.marginBottom = parseInt(bottom, 10);
    if (left) layout.marginLeft = parseInt(left, 10);
    if (right) layout.marginRight = parseInt(right, 10);
  }

  return layout;
}

/* ------------------------------------------------------------------ */
/* ZIP 파트 읽기                                                        */
/* ------------------------------------------------------------------ */

async function readPart(zip: JSZip, path: string): Promise<string | null> {
  const file = zip.file(path);
  if (!file) return null;
  return await file.async('string');
}

/* ------------------------------------------------------------------ */
/* 테마 폰트 파싱                                                       */
/* ------------------------------------------------------------------ */

interface ThemeFonts {
  major?: string; // majorFont (Heading 등)
  minor?: string; // minorFont (Body 등)
}

const A_THEME_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';

function parseThemeFonts(xml: string): ThemeFonts {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const result: ThemeFonts = {};

  // majorFont → <a:latin typeface="..."/>
  const majorEls = dom.getElementsByTagNameNS(A_THEME_NS, 'majorFont');
  if (majorEls[0]) {
    const latin = majorEls[0].getElementsByTagNameNS(A_THEME_NS, 'latin')[0];
    if (latin) {
      const typeface = latin.getAttribute('typeface');
      if (typeface) result.major = typeface;
    }
  }

  // minorFont → <a:latin typeface="..."/>
  const minorEls = dom.getElementsByTagNameNS(A_THEME_NS, 'minorFont');
  if (minorEls[0]) {
    const latin = minorEls[0].getElementsByTagNameNS(A_THEME_NS, 'latin')[0];
    if (latin) {
      const typeface = latin.getAttribute('typeface');
      if (typeface) result.minor = typeface;
    }
  }

  return result;
}

/* ------------------------------------------------------------------ */
/* _rels 파싱 — rId → Target 매핑                                       */
/* ------------------------------------------------------------------ */

function parseRels(xml: string): Map<string, string> {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const map = new Map<string, string>();

  const relEls = dom.getElementsByTagNameNS(REL_NS, 'Relationship');
  for (let i = 0; i < relEls.length; i++) {
    const el = relEls[i];
    const id = el.getAttribute('Id');
    const target = el.getAttribute('Target');
    if (id && target) {
      map.set(id, target);
    }
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* 미디어 로드                                                          */
/* ------------------------------------------------------------------ */

async function loadMedia(zip: JSZip, rels: Map<string, string>): Promise<Map<string, string>> {
  const media = new Map<string, string>();

  for (const [rId, target] of rels) {
    if (!target.startsWith('media/') && !target.includes('/media/')) continue;

    const filePath = target.startsWith('/') ? target.slice(1) : `word/${target}`;
    const file = zip.file(filePath);
    if (!file) continue;

    const data = await file.async('base64');
    const ext = target.split('.').pop()?.toLowerCase() || 'png';
    const mimeMap: Record<string, string> = {
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      bmp: 'image/bmp',
      svg: 'image/svg+xml',
      emf: 'image/x-emf',
      wmf: 'image/x-wmf',
      tiff: 'image/tiff',
      tif: 'image/tiff',
    };
    const mime = mimeMap[ext] || 'image/png';
    media.set(rId, `data:${mime};base64,${data}`);
  }

  return media;
}

/* ------------------------------------------------------------------ */
/* styles.xml 파싱 (docDefaults 포함)                                    */
/* ------------------------------------------------------------------ */

function parseStyles(
  xml: string,
  themeFonts?: ThemeFonts,
): {
  styles: Map<string, DocxStyle>;
  docDefaults?: DocxDocument['docDefaults'];
} {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const map = new Map<string, DocxStyle>();

  // ── 테마 폰트 해석 헬퍼 ──
  const resolveThemeFont = (themeAttr: string | null): string | undefined => {
    if (!themeAttr || !themeFonts) return undefined;
    // majorHAnsi, majorEastAsia, majorBidi → major
    if (themeAttr.startsWith('major')) return themeFonts.major;
    // minorHAnsi, minorEastAsia, minorBidi → minor
    if (themeAttr.startsWith('minor')) return themeFonts.minor;
    return undefined;
  };

  // ── docDefaults 파싱 ──
  let docDefaults: DocxDocument['docDefaults'] | undefined;
  const docDefaultsEl = dom.getElementsByTagNameNS(W_NS, 'docDefaults')[0];
  if (docDefaultsEl) {
    docDefaults = {};

    // rPrDefault → 기본 런 속성
    const rPrDefault = getW(docDefaultsEl, 'rPrDefault');
    if (rPrDefault) {
      const rPr = getW(rPrDefault, 'rPr');
      if (rPr) {
        // 폰트 크기: w:sz 또는 w:szCs
        const szEl = getW(rPr, 'sz');
        const szCsEl = getW(rPr, 'szCs');
        const szVal = szEl
          ? szEl.getAttributeNS(W_NS, 'val') || szEl.getAttribute('w:val')
          : null;
        const szCsVal = szCsEl
          ? szCsEl.getAttributeNS(W_NS, 'val') || szCsEl.getAttribute('w:val')
          : null;
        if (szVal) {
          docDefaults.fontSize = parseInt(szVal, 10);
        } else if (szCsVal) {
          docDefaults.fontSize = parseInt(szCsVal, 10);
        }

        // 폰트 패밀리: 직접 지정 → 테마 참조 폴백
        const rFontsEl = getW(rPr, 'rFonts');
        if (rFontsEl) {
          // 1) 직접 폰트 이름
          const directFont =
            rFontsEl.getAttributeNS(W_NS, 'ascii') ||
            rFontsEl.getAttribute('w:ascii') ||
            rFontsEl.getAttributeNS(W_NS, 'hAnsi') ||
            rFontsEl.getAttribute('w:hAnsi') ||
            rFontsEl.getAttributeNS(W_NS, 'eastAsia') ||
            rFontsEl.getAttribute('w:eastAsia') ||
            undefined;

          if (directFont) {
            docDefaults.fontFamily = directFont;
          } else {
            // 2) 테마 참조 폴백
            const themeAttr =
              rFontsEl.getAttributeNS(W_NS, 'asciiTheme') ||
              rFontsEl.getAttribute('w:asciiTheme') ||
              rFontsEl.getAttributeNS(W_NS, 'hAnsiTheme') ||
              rFontsEl.getAttribute('w:hAnsiTheme') ||
              rFontsEl.getAttributeNS(W_NS, 'eastAsiaTheme') ||
              rFontsEl.getAttribute('w:eastAsiaTheme') ||
              null;
            const resolved = resolveThemeFont(themeAttr);
            if (resolved) docDefaults.fontFamily = resolved;
          }
        }
      }
    }

    // pPrDefault → 기본 단락 속성
    const pPrDefault = getW(docDefaultsEl, 'pPrDefault');
    if (pPrDefault) {
      const pPr = getW(pPrDefault, 'pPr');
      if (pPr) {
        // 기본 간격
        docDefaults.spacing = parseSpacing(pPr);

        // 기본 정렬
        const jcVal = getWVal(pPr, 'jc');
        if (jcVal) {
          docDefaults.alignment = jcVal;
        }
      }
    }
  }

  // ── 스타일 파싱 ──
  const styleEls = dom.getElementsByTagNameNS(W_NS, 'style');
  for (let i = 0; i < styleEls.length; i++) {
    const el = styleEls[i];
    const id = el.getAttributeNS(W_NS, 'styleId') || el.getAttribute('w:styleId');
    if (!id) continue;

    const style: DocxStyle = { id };

    const nameEl = getW(el, 'name');
    if (nameEl) style.name = nameEl.getAttributeNS(W_NS, 'val') || nameEl.getAttribute('w:val') || undefined;

    const basedOnEl = getW(el, 'basedOn');
    if (basedOnEl) style.basedOn = basedOnEl.getAttributeNS(W_NS, 'val') || basedOnEl.getAttribute('w:val') || undefined;

    // 런 속성 (rPr)
    const rPr = getW(el, 'rPr');
    if (rPr) {
      const runProps = parseRunProperties(rPr);
      Object.assign(style, runProps);
    }

    // 단락 속성 (pPr)
    const pPr = directChildren(el, W_NS, 'pPr')[0];
    if (pPr) {
      style.alignment = getWVal(pPr, 'jc');
      style.indent = parseIndent(pPr);
      style.spacing = parseSpacing(pPr);
      const styleNumbering = parseNumberingProperties(pPr);
      // OOXML: paragraph style의 ilvl은 무시하고 numbering.xml의 pStyle로 level을 찾는다.
      if (styleNumbering?.numId !== undefined) {
        style.numbering = { numId: styleNumbering.numId };
      }
    }

    map.set(id, style);
  }

  return { styles: map, docDefaults };
}

/* ------------------------------------------------------------------ */
/* numbering.xml 파싱                                                  */
/* ------------------------------------------------------------------ */

function parseNumbering(xml: string): Map<string, DocxNumbering> {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const map = new Map<string, DocxNumbering>();

  // abstractNum 먼저 파싱
  const abstractNums = new Map<string, Map<number, DocxNumberingLevel>>();
  const abstractEls = dom.getElementsByTagNameNS(W_NS, 'abstractNum');
  for (let i = 0; i < abstractEls.length; i++) {
    const el = abstractEls[i];
    const abstractNumId = el.getAttributeNS(W_NS, 'abstractNumId') || el.getAttribute('w:abstractNumId');
    if (!abstractNumId) continue;

    const levels = new Map<number, DocxNumberingLevel>();
    const lvlEls = getAllW(el, 'lvl');
    for (const lvlEl of lvlEls) {
      const ilvl = parseInt(lvlEl.getAttributeNS(W_NS, 'ilvl') || lvlEl.getAttribute('w:ilvl') || '0', 10);
      const format = getWVal(lvlEl, 'numFmt') || 'decimal';
      const text = getWVal(lvlEl, 'lvlText') || '';
      const paragraphStyleId = getDirectWVal(lvlEl, 'pStyle');

      // start value (기본값 1)
      const startVal = getWVal(lvlEl, 'start');
      const start = startVal ? parseInt(startVal, 10) : 1;

      let indent: number | undefined;
      let hanging: number | undefined;
      const pPr = getW(lvlEl, 'pPr');
      if (pPr) {
        const indEl = getW(pPr, 'ind');
        if (indEl) {
          const left = indEl.getAttributeNS(W_NS, 'left') || indEl.getAttribute('w:left');
          if (left) indent = twipsToPx(parseInt(left, 10));

          const hangingVal = indEl.getAttributeNS(W_NS, 'hanging') || indEl.getAttribute('w:hanging');
          if (hangingVal) hanging = twipsToPx(parseInt(hangingVal, 10));
        }
      }

      levels.set(ilvl, { format, text, indent, start, hanging, paragraphStyleId });
    }
    abstractNums.set(abstractNumId, levels);
  }

  // num → abstractNumId 매핑
  const numEls = dom.getElementsByTagNameNS(W_NS, 'num');
  for (let i = 0; i < numEls.length; i++) {
    const el = numEls[i];
    const numId = el.getAttributeNS(W_NS, 'numId') || el.getAttribute('w:numId');
    if (!numId) continue;

    const abstractNumIdEl = getW(el, 'abstractNumId');
    const abstractNumId = abstractNumIdEl?.getAttributeNS(W_NS, 'val') || abstractNumIdEl?.getAttribute('w:val');
    if (!abstractNumId) continue;

    const levels = abstractNums.get(abstractNumId) || new Map();
    map.set(numId, { numId, levels });
  }

  return map;
}

/* ------------------------------------------------------------------ */
/* 본문(body) 파싱 — numbering counter tracking 포함                     */
/* ------------------------------------------------------------------ */

function parseBody(
  bodyEl: Element,
  rels: Map<string, string>,
  numbering: Map<string, DocxNumbering>,
  styles: Map<string, DocxStyle>,
): DocxBlock[] {
  const blocks: DocxBlock[] = [];

  const counterState: NumberingCounterState = {
    counters: new Map<string, number>(),
    prevNumIdByLevel: new Map<number, string>(),
  };

  for (let i = 0; i < bodyEl.childNodes.length; i++) {
    const node = bodyEl.childNodes[i];
    if (node.nodeType !== 1) continue;
    const el = node as Element;

    if (el.localName === 'p' && el.namespaceURI === W_NS) {
      const para = parseParagraph(el, rels, styles, numbering);
      assignNumberingIndex(para, numbering, counterState);

      blocks.push(para);
    } else if (el.localName === 'tbl' && el.namespaceURI === W_NS) {
      blocks.push(parseTable(el, rels, styles, numbering, counterState));
    }
  }

  return blocks;
}

interface NumberingCounterState {
  counters: Map<string, number>;
  prevNumIdByLevel: Map<number, string>;
}

/** 파싱 순서대로 목록 번호를 계산한다. numId=0은 명시적인 목록 해제다. */
function assignNumberingIndex(
  para: DocxParagraph,
  numbering: Map<string, DocxNumbering>,
  state: NumberingCounterState,
): void {
  if (!para.numbering || para.numbering.numId === '0') return;

  const { numId, level } = para.numbering;
  const counterKey = `${numId}_${level}`;

  // 같은 level에서 numId가 변경되면 해당 카운터 리셋
  const prevNumId = state.prevNumIdByLevel.get(level);
  if (prevNumId !== undefined && prevNumId !== numId) {
    state.counters.delete(`${prevNumId}_${level}`);
  }
  state.prevNumIdByLevel.set(level, numId);

  // 카운터가 없으면 numbering level의 start 값으로 초기화
  if (!state.counters.has(counterKey)) {
    const numDef = numbering.get(numId);
    const lvlDef = numDef?.levels.get(level);
    state.counters.set(counterKey, lvlDef?.start ?? 1);
  } else {
    state.counters.set(counterKey, state.counters.get(counterKey)! + 1);
  }

  para.numbering.index = state.counters.get(counterKey)!;
}

/* ------------------------------------------------------------------ */
/* 단락(paragraph) 파싱                                                 */
/* ------------------------------------------------------------------ */

function parseParagraph(
  pEl: Element,
  rels: Map<string, string>,
  styles: Map<string, DocxStyle>,
  numbering: Map<string, DocxNumbering>,
): DocxParagraph {
  const para: DocxParagraph = {
    type: 'paragraph',
    id: generateId(),
    runs: [],
  };

  // 단락 속성 (pPr)
  const pPr = directChildren(pEl, W_NS, 'pPr')[0];
  if (pPr) {
    para.styleId = getDirectWVal(pPr, 'pStyle');
    para.alignment = getWVal(pPr, 'jc');
    para.indent = parseIndent(pPr);
    para.spacing = parseSpacing(pPr);
    // 문단 기본 런 속성 (문단 내 런에 공통 상속)
    const pPrRPr = getW(pPr, 'rPr');
    if (pPrRPr) {
      para.runProps = parseRunProperties(pPrRPr);
    }

    // 번호 매기기: 문단의 partial numPr가 스타일 값을 필드 단위로 덮어쓴다.
    const styleNumbering = resolveStyleNumbering(para.styleId, styles);
    const directNumbering = parseNumberingProperties(pPr);
    const effectiveNumbering = directNumbering !== undefined
      ? { ...styleNumbering, ...directNumbering }
      : styleNumbering;
    if (effectiveNumbering?.numId !== undefined) {
      const styleLevel = resolveStyleNumberingLevel(
        para.styleId,
        styles,
        numbering.get(effectiveNumbering.numId),
      );
      para.numbering = {
        numId: effectiveNumbering.numId,
        level: directNumbering?.level ?? styleLevel ?? 0,
        index: 0,
      };
    }
  }

  // 런 파싱
  for (let i = 0; i < pEl.childNodes.length; i++) {
    const node = pEl.childNodes[i];
    if (node.nodeType !== 1) continue;
    const el = node as Element;

    if (el.localName === 'r' && el.namespaceURI === W_NS) {
      const runs = parseRun(el, rels);
      para.runs.push(...runs);
    } else if (el.localName === 'hyperlink' && el.namespaceURI === W_NS) {
      // 하이퍼링크 내부 런 처리
      const innerRuns = directChildren(el, W_NS, 'r');
      for (const innerR of innerRuns) {
        const runs = parseRun(innerR, rels);
        para.runs.push(...runs);
      }
    }
  }

  return para;
}

/* ------------------------------------------------------------------ */
/* 런(run) 파싱                                                        */
/* ------------------------------------------------------------------ */

function parseRun(rEl: Element, rels: Map<string, string>): DocxRun[] {
  const runs: DocxRun[] = [];

  // 런 속성 (rPr)
  const rPr = directChildren(rEl, W_NS, 'rPr')[0];
  const baseProps = rPr ? parseRunProperties(rPr) : {};

  for (let i = 0; i < rEl.childNodes.length; i++) {
    const node = rEl.childNodes[i];
    if (node.nodeType !== 1) continue;
    const el = node as Element;

    if (el.localName === 't' && el.namespaceURI === W_NS) {
      // 텍스트
      runs.push({
        type: 'text',
        id: generateId(),
        text: el.textContent || '',
        ...baseProps,
      });
    } else if (el.localName === 'br' && el.namespaceURI === W_NS) {
      // 줄바꿈
      runs.push({ type: 'break', id: generateId() });
    } else if (el.localName === 'tab' && el.namespaceURI === W_NS) {
      // 탭
      runs.push({ type: 'tab', id: generateId(), text: '\t' });
    } else if (el.localName === 'drawing' && el.namespaceURI === W_NS) {
      // 이미지
      const imgRun = parseDrawing(el, rels);
      if (imgRun) {
        runs.push({ ...imgRun, ...baseProps, type: 'image' });
      }
    }
  }

  return runs;
}

/* ------------------------------------------------------------------ */
/* 런 속성(rPr) 파싱                                                    */
/* ------------------------------------------------------------------ */

function parseRunProperties(rPr: Element): Partial<DocxRun> {
  const props: Partial<DocxRun> = {};

  // 볼드
  const bEl = getW(rPr, 'b');
  if (bEl) {
    const val = bEl.getAttributeNS(W_NS, 'val') || bEl.getAttribute('w:val');
    props.bold = val !== '0' && val !== 'false';
  }

  // 이탤릭
  const iEl = getW(rPr, 'i');
  if (iEl) {
    const val = iEl.getAttributeNS(W_NS, 'val') || iEl.getAttribute('w:val');
    props.italic = val !== '0' && val !== 'false';
  }

  // 밑줄
  const uEl = getW(rPr, 'u');
  if (uEl) {
    const val = uEl.getAttributeNS(W_NS, 'val') || uEl.getAttribute('w:val');
    props.underline = val !== 'none' && val !== undefined;
  }

  // 취소선
  const strikeEl = getW(rPr, 'strike');
  if (strikeEl) {
    const val = strikeEl.getAttributeNS(W_NS, 'val') || strikeEl.getAttribute('w:val');
    props.strike = val !== '0' && val !== 'false';
  }

  // 폰트 크기 (half-points): w:sz 우선, 없으면 w:szCs
  const szEl = getW(rPr, 'sz');
  if (szEl) {
    const val = szEl.getAttributeNS(W_NS, 'val') || szEl.getAttribute('w:val');
    if (val) props.fontSize = parseInt(val, 10);
  } else {
    const szCsEl = getW(rPr, 'szCs');
    if (szCsEl) {
      const val = szCsEl.getAttributeNS(W_NS, 'val') || szCsEl.getAttribute('w:val');
      if (val) props.fontSize = parseInt(val, 10);
    }
  }

  // 폰트 패밀리
  const rFontsEl = getW(rPr, 'rFonts');
  if (rFontsEl) {
    props.fontFamily =
      rFontsEl.getAttributeNS(W_NS, 'ascii') ||
      rFontsEl.getAttribute('w:ascii') ||
      rFontsEl.getAttributeNS(W_NS, 'hAnsi') ||
      rFontsEl.getAttribute('w:hAnsi') ||
      rFontsEl.getAttributeNS(W_NS, 'eastAsia') ||
      rFontsEl.getAttribute('w:eastAsia') ||
      undefined;
  }

  // 색상
  const colorEl = getW(rPr, 'color');
  if (colorEl) {
    const val = colorEl.getAttributeNS(W_NS, 'val') || colorEl.getAttribute('w:val');
    if (val && val !== 'auto') props.color = `#${val}`;
  }

  // 하이라이트
  const highlightEl = getW(rPr, 'highlight');
  if (highlightEl) {
    props.highlight = highlightEl.getAttributeNS(W_NS, 'val') || highlightEl.getAttribute('w:val') || undefined;
  }

  return props;
}

/* ------------------------------------------------------------------ */
/* 들여쓰기 파싱                                                        */
/* ------------------------------------------------------------------ */

function parseIndent(pPr: Element): DocxParagraph['indent'] | undefined {
  const indEl = getW(pPr, 'ind');
  if (!indEl) return undefined;

  const left = indEl.getAttributeNS(W_NS, 'left') || indEl.getAttribute('w:left');
  const right = indEl.getAttributeNS(W_NS, 'right') || indEl.getAttribute('w:right');
  const firstLine = indEl.getAttributeNS(W_NS, 'firstLine') || indEl.getAttribute('w:firstLine');
  const hanging = indEl.getAttributeNS(W_NS, 'hanging') || indEl.getAttribute('w:hanging');

  if (!left && !right && !firstLine && !hanging) return undefined;

  return {
    left: left ? twipsToPx(parseInt(left, 10)) : undefined,
    right: right ? twipsToPx(parseInt(right, 10)) : undefined,
    firstLine: firstLine ? twipsToPx(parseInt(firstLine, 10)) : undefined,
    hanging: hanging ? twipsToPx(parseInt(hanging, 10)) : undefined,
  };
}

/* ------------------------------------------------------------------ */
/* 간격 파싱 (lineRule 포함)                                             */
/* ------------------------------------------------------------------ */

function parseSpacing(pPr: Element): DocxParagraph['spacing'] | undefined {
  const spEl = getW(pPr, 'spacing');
  if (!spEl) return undefined;

  const before = spEl.getAttributeNS(W_NS, 'before') || spEl.getAttribute('w:before');
  const after = spEl.getAttributeNS(W_NS, 'after') || spEl.getAttribute('w:after');
  const line = spEl.getAttributeNS(W_NS, 'line') || spEl.getAttribute('w:line');
  const lineRule = spEl.getAttributeNS(W_NS, 'lineRule') || spEl.getAttribute('w:lineRule');

  if (!before && !after && !line && !lineRule) return undefined;

  return {
    before: before ? twipsToPx(parseInt(before, 10)) : undefined,
    after: after ? twipsToPx(parseInt(after, 10)) : undefined,
    line: line ? parseInt(line, 10) : undefined,
    lineRule: lineRule || undefined,
  };
}

/* ------------------------------------------------------------------ */
/* 이미지(drawing) 파싱                                                 */
/* ------------------------------------------------------------------ */

function parseDrawing(drawingEl: Element, rels: Map<string, string>): DocxRun | null {
  // inline 또는 anchor
  const inline =
    drawingEl.getElementsByTagNameNS(WP_NS, 'inline')[0] ||
    drawingEl.getElementsByTagNameNS(WP_NS, 'anchor')[0];
  if (!inline) return null;

  // 크기 (extent)
  const extent = inline.getElementsByTagNameNS(WP_NS, 'extent')[0];
  let width: number | undefined;
  let height: number | undefined;
  if (extent) {
    const cx = extent.getAttribute('cx');
    const cy = extent.getAttribute('cy');
    if (cx) width = emuToPx(parseInt(cx, 10));
    if (cy) height = emuToPx(parseInt(cy, 10));
  }

  // blip (이미지 참조)
  const blip = drawingEl.getElementsByTagNameNS(A_NS, 'blip')[0];
  if (!blip) return null;

  const rEmbed = blip.getAttributeNS(R_NS, 'embed') || blip.getAttribute('r:embed');
  if (!rEmbed) return null;

  return {
    type: 'image',
    id: generateId(),
    imageId: rEmbed,
    imageWidth: width,
    imageHeight: height,
  };
}

/* ------------------------------------------------------------------ */
/* 테이블 파싱                                                          */
/* ------------------------------------------------------------------ */

function parseTable(
  tblEl: Element,
  rels: Map<string, string>,
  styles: Map<string, DocxStyle>,
  numbering: Map<string, DocxNumbering>,
  counterState: NumberingCounterState,
): DocxTable {
  const table: DocxTable = {
    type: 'table',
    id: generateId(),
    rows: [],
  };

  // 테이블 속성 (tblPr)
  const tblPr = directChildren(tblEl, W_NS, 'tblPr')[0];
  if (tblPr) {
    const tblBorders = getW(tblPr, 'tblBorders');
    if (tblBorders) {
      table.borders = parseTableBorders(tblBorders);
    }
  }

  const trEls = directChildren(tblEl, W_NS, 'tr');
  for (const trEl of trEls) {
    const row: DocxTableRow = { cells: [] };

    const tcEls = directChildren(trEl, W_NS, 'tc');
    for (const tcEl of tcEls) {
      const cell: DocxTableCell = { paragraphs: [] };

      // 셀 속성 (tcPr)
      const tcPr = directChildren(tcEl, W_NS, 'tcPr')[0];
      if (tcPr) {
        // colspan (gridSpan)
        const gridSpan = getW(tcPr, 'gridSpan');
        if (gridSpan) {
          const val = gridSpan.getAttributeNS(W_NS, 'val') || gridSpan.getAttribute('w:val');
          if (val) cell.colSpan = parseInt(val, 10);
        }

        // 셀 배경색 (shading)
        const shd = getW(tcPr, 'shd');
        if (shd) {
          const fill = shd.getAttributeNS(W_NS, 'fill') || shd.getAttribute('w:fill');
          if (fill && fill !== 'auto') cell.shading = `#${fill}`;
        }

        // 셀 너비
        const tcW = getW(tcPr, 'tcW');
        if (tcW) {
          const w = tcW.getAttributeNS(W_NS, 'w') || tcW.getAttribute('w:w');
          if (w) cell.width = twipsToPx(parseInt(w, 10));
        }

        // 테두리
        const tcBorders = getW(tcPr, 'tcBorders');
        if (tcBorders) {
          cell.borders = parseBorders(tcBorders);
        }
      }

      // 셀 내부 단락
      const pEls = directChildren(tcEl, W_NS, 'p');
      for (const pEl of pEls) {
        const paragraph = parseParagraph(pEl, rels, styles, numbering);
        assignNumberingIndex(paragraph, numbering, counterState);
        cell.paragraphs.push(paragraph);
      }

      row.cells.push(cell);
    }

    table.rows.push(row);
  }

  return table;
}

/* ------------------------------------------------------------------ */
/* 테두리 파싱                                                          */
/* ------------------------------------------------------------------ */

function parseBorders(bordersEl: Element): DocxTableCell['borders'] {
  const borders: DocxTableCell['borders'] = {};
  const sides = ['top', 'bottom', 'left', 'right'] as const;

  for (const side of sides) {
    const el = getW(bordersEl, side);
    if (!el) continue;

    const style = el.getAttributeNS(W_NS, 'val') || el.getAttribute('w:val');
    const color = el.getAttributeNS(W_NS, 'color') || el.getAttribute('w:color');
    const sz = el.getAttributeNS(W_NS, 'sz') || el.getAttribute('w:sz');

    borders[side] = {
      style: style || undefined,
      color: color && color !== 'auto' ? `#${color}` : undefined,
      size: sz ? parseInt(sz, 10) : undefined,
    };
  }

  return borders;
}

function parseTableBorders(
  bordersEl: Element,
): DocxTable['borders'] {
  const borders: NonNullable<DocxTable['borders']> = {};
  const sides = ['top', 'bottom', 'left', 'right', 'insideH', 'insideV'] as const;

  for (const side of sides) {
    const el = getW(bordersEl, side);
    if (!el) continue;

    const style = el.getAttributeNS(W_NS, 'val') || el.getAttribute('w:val');
    const color = el.getAttributeNS(W_NS, 'color') || el.getAttribute('w:color');
    const sz = el.getAttributeNS(W_NS, 'sz') || el.getAttribute('w:sz');

    borders[side] = {
      style: style || undefined,
      color: color && color !== 'auto' ? `#${color}` : undefined,
      size: sz ? parseInt(sz, 10) : undefined,
    };
  }

  return borders;
}

/* ------------------------------------------------------------------ */
/* Exports (유틸리티 함수들도 외부에서 사용 가능)                            */
/* ------------------------------------------------------------------ */

export { emuToPx, halfPointsToPx };
